import { cancelOnSessionChange } from './session-cancellation';
// src/app/state/effects/user.effects.ts
import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import * as UserActions from '../actions/user.actions';
import { mergeMap, map, catchError, takeUntil } from 'rxjs/operators';
import { of, Subject } from 'rxjs';
import { UsersService } from '@api/services/users.service';
import { ApiDefUserData } from '@api/models/api-def-user-data';

@Injectable()
export class UserEffects {
  loadUserById$;
  loadUsers$;
  updateUser$;
  deleteUser$;
  loadUserByEmail$;

  private readonly loadUsersCancel$ = new Subject<void>();

  constructor(
    private actions$: Actions,
    private usersService: UsersService
  ) {
    this.loadUserById$ = createEffect(() =>
      this.actions$.pipe(
        ofType(UserActions.loadUserById),
        mergeMap(({ userId }) =>
          this.usersService.usersGetUserById({ id: userId }).pipe(
            map((user: ApiDefUserData) => UserActions.loadUserByIdSuccess({ userId, userData: user })),
            catchError((error) =>
              of(UserActions.loadUserByIdFailure({ userId, error: error.message || 'Failed to load user' }))
            ),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    this.loadUsers$ = createEffect(() =>
      this.actions$.pipe(
        ofType(UserActions.loadUsers, UserActions.cancelLoadUsers),
        mergeMap(action => {
          if (action.type === '[User] Cancel Load Users') {
            this.loadUsersCancel$.next();
            return of(); // No further action
          }
          // Only handle loadUsers actions here
          if (action.type === '[User] Load Users') {
            return this.usersService.usersGetUsers(action.params).pipe(
              takeUntil(this.loadUsersCancel$),
              map((result: { users?: ApiDefUserData[]; total?: number }) =>
                UserActions.loadUsersSuccess({ users: result.users || [], total: result.total || 0 })
              ),
              catchError((error) =>
                of(UserActions.loadUsersFailure({ error: error.message || 'Failed to load users' }))
              ),
            cancelOnSessionChange(this.actions$)
            );
          }
          return of();
        })
      )
    );

    this.updateUser$ = createEffect(() =>
      this.actions$.pipe(
        ofType(UserActions.updateUser),
        mergeMap(({ userId, changes }) =>
          this.usersService.usersUpdateUserById({ id: userId, body: changes }).pipe(
            map((user: ApiDefUserData) => UserActions.updateUserSuccess({ userId, userData: user })),
            catchError((error) =>
              of(UserActions.updateUserFailure({ userId, error: error.message || 'Failed to update user' }))
            ),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    this.deleteUser$ = createEffect(() =>
      this.actions$.pipe(
        ofType(UserActions.deleteUser),
        mergeMap(({ userId }) =>
          this.usersService.usersDeleteUserById({ id: userId }).pipe(
            map(() => UserActions.deleteUserSuccess({ userId })),
            catchError((error) =>
              of(UserActions.deleteUserFailure({ userId, error: error.message || 'Failed to delete user' }))
            ),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    this.loadUserByEmail$ = createEffect(() =>
      this.actions$.pipe(
        ofType(UserActions.loadUserByEmail),
        mergeMap(({ email }) =>
          this.usersService.usersGetUsers({ search: email }).pipe(
            map((result: { users?: ApiDefUserData[] }) => {
              const user = (result.users || []).find(u => u.email === email);
              if (user) {
                return UserActions.loadUserByEmailSuccess({ userData: user });
              } else {
                return UserActions.loadUserByEmailFailure({ email, error: 'User not found' });
              }
            }),
            catchError((error) =>
              of(UserActions.loadUserByEmailFailure({ email, error: error.message || 'Failed to load user by email' }))
            ),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );
  }
}
